import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faClose, faInfo, faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import moment from "moment-timezone";
import StatusCell from "./StatusCell";
import { useAccess } from "../../../utils/hooks/access";
import { useCompanyDateFormat } from "../../../utils/hooks/company";
import { renderSettingsResourceLabel } from "../../../utils/helpers/settings";

const columnHelper = createColumnHelper();


export const useColumns = ({ openDetailsModal, openApproveDenyModal, selectedStatus }) => {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const { hasAccess: canApprove } = useAccess("timeSheet.canApproveRetro");

    return useMemo(() => {
        const columns = [
            columnHelper.accessor("user", {
                header: t("user"),
                cell: (info) => {
                    const value = info.getValue();
                    return `${value.lastName}, ${value.firstName}`;
                }
            }),
            columnHelper.accessor("user.employeeNumber", {
                header: t("employee-number"),
            }),
            columnHelper.accessor("payGroup", {
                header: t("pay-group"),
                cell: (info) => {
                    const value = info.getValue();
                    return (typeof value !== "string" && value) ? renderSettingsResourceLabel(value) : "--";
                }
            }),
            columnHelper.accessor("startDate", {
                header: t("start-date"),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? moment(value).format(dateFormat) : "--";
                }
            }),
            columnHelper.accessor("endDate", {
                header: t("end-date"),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? moment(value).format(dateFormat) : "--";
                }
            }),
            columnHelper.accessor("paidOnPeriodStartDate", {
                header: t("paid-on-period-start-date"),
                visible: ['applied', 'approved'].includes(selectedStatus),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? moment(value).format(dateFormat) : "--";
                }
            }),
            columnHelper.accessor("paidOnPeriodEndDate", {
                header: t("paid-on-period-end-date"),
                visible: ['applied', 'approved'].includes(selectedStatus),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? moment(value).format(dateFormat) : "--";
                }
            }),
            // ...(['applied', 'approved'].includes(selectedStatus) ? [
            //     columnHelper.accessor("paidOnPeriodStartDate", {
            //         header: t("paid-on-period-start-date"),
            //         cell: (info) => {
            //             const value = info.getValue();
            //             return moment(value).format(dateFormat);
            //         }
            //     }),
            //     columnHelper.accessor("paidOnPeriodEndDate", {
            //         header: t("paid-on-period-end-date"),
            //         cell: (info) => {
            //             const value = info.getValue();
            //             return moment(value).format(dateFormat);
            //         }
            //     }),
            // ] : []),
            columnHelper.accessor("adjustments", {
                header: t("adjustments"),
                cell: ({ row: { original } }) => {
                    return original?.retros?.length || 0;
                }
            }),
            columnHelper.accessor("status", {
                header: t("status"),
                cell: ({ row: { original } }) => {
                    return <StatusCell original={original} />
                }
            }),
            columnHelper.display({
                header: t("actions"),
                cell: ({ row: { original } }) => {
                    return (
                        <div className="d-flex gap-3 items-center">
                            <FontAwesomeIcon
                                className="cursor-pointer text-lg"
                                icon={faInfo}
                                onClick={() => openDetailsModal({ data: [original], user: original?.user })}
                            />
                            {(canApprove && original?.status === "pending") && (
                                <>
                                    <FontAwesomeIcon
                                        className="cursor-pointer text-danger text-lg"
                                        title={t("reject-retro-adjustment")}
                                        icon={faClose}
                                        onClick={() => openApproveDenyModal({ ...original, type: "reject" })}
                                    />
                                    <FontAwesomeIcon
                                        className="cursor-pointer text-green text-lg"
                                        title={t("approve-retro-adjustment")}
                                        icon={faCheck}
                                        onClick={() => openApproveDenyModal({ ...original, type: "approve" })}
                                    />
                                </>
                            )}
                            {(canApprove && original?.status === "approved") && (
                                <>
                                    <FontAwesomeIcon
                                        className="cursor-pointer text-orange text-lg"
                                        title={t("reopen-retro-adjustment")}
                                        icon={faRotateLeft}
                                        onClick={() => openApproveDenyModal({ ...original, type: "reopen" })}
                                    />
                                </>
                            )}
                        </div>
                    )
                }
            })
        ];
        return columns;
    }, [canApprove, dateFormat, openApproveDenyModal, openDetailsModal, selectedStatus, t]);
}